/* eslint-disable complexity */
/* eslint-disable no-restricted-imports */
/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { useSettings, useSingleSettings } from '@/data/Settings';
import { ID, TransactionErrorResponse } from '@/data/types/Basic';
import { RequestParams } from 'integration/generated/transactions/http-client';
import { transactionsLoginIdentity } from 'integration/generated/transactions';
import { ComIbmCommerceRestMemberHandlerLoginIdentityHandlerLoginForm } from 'integration/generated/transactions/data-contracts';
import { isErrorType, processError } from '@/data/utils/processError';
import { useNotifications } from '@/data/Content/Notifications';
import { ErrorType } from '@/data/types/Error';
import { ACCOUNT_LOCKOUT_ERROR, PASSWORD_EXPIRED } from '@/data/constants/errors';
import { useCallback, useContext, useMemo, useReducer, useState } from 'react';
import { useExtraRequestParameters } from '@/data/Content/_ExtraRequestParameters';
import { getLocalization } from '@/data/Localization';
import { ContentProps } from '@/data/types/ContentProps';
import { GetServerSidePropsContext } from 'next';
import { error as logError } from '@/data/utils/loggerUtil';

import { isValidEmail } from '@/data/utils/isValidEmail';
import getValueByPathFromUnknown from 'integration/utils/getValueByPathFromUnknown';
import { EventsContext } from '@/data/context/events';
import { useLoginOrigin } from '@/data/context/loginOrigin';
import { formatLogonId } from '@/data/utils/formatLogonId';
import { useIsLoadingBarNextRoute } from 'integration/hooks/useIsLoadingNextRoute';
import { useGetOriginPathQuery } from 'integration/hooks/useGetOriginPathQuery';
import { usePostLoginDialog } from '@/data/context/postLoginDialog';
import {
	readStoredLoginMethod,
	removeStoredLoginMethod,
	type StoredLoginMethod,
	writeStoredLoginMethod,
} from '@/data/utils/loginLastMethod';
import { decodeGoogleIdToken } from '@/data/utils/decodeGoogleIdToken';
import { AuthMethod } from '@/data/types/GTM';

export { personMutatorKeyMatcher } from '@/data/utils/mutatorKeyMatchers/personMutatorKeyMatcher';

export const loginFetcher =
	(pub: boolean, context?: GetServerSidePropsContext) =>
	async (
		storeId: string,
		query: {
			[key: string]: string | boolean | ID[] | number;
		},
		data: ComIbmCommerceRestMemberHandlerLoginIdentityHandlerLoginForm,
		params: RequestParams
	) => {
		try {
			const resp = await transactionsLoginIdentity(pub).loginIdentityLogin(
				storeId,
				query,
				data,
				params
			);
			return resp;
		} catch (error: any) {
			logError(context?.req, 'Login: loginFetcher: error: %o', error);
			throw error;
		}
	};

export type UserLogon = {
	logonId?: string;
	email?: string;
	logonPassword: string;
	rememberMe: boolean;
	logonPasswordNew?: string;
	logonPasswordVerify?: string;
	changePassword?: string;
	x_recaptchaToken?: string;
	x_otpFlow?: string;
	extendedProperties?: string;
	googleIdToken?: string;
};

export const getLogin = async ({ cache, context }: ContentProps) => {
	await Promise.all([
		getLocalization(cache, context.locale || 'en-US', 'SignIn'),
		getLocalization(cache, context.locale || 'en-US', 'RegistrationB2BLayout'),
	]);
};

export const useLogin = () => {
	const { settings } = useSettings();
	const params = useExtraRequestParameters();
	const { notifyError } = useNotifications();
	const originPathQuery = useGetOriginPathQuery();
	const { activate: activatePostLoginDialog } = usePostLoginDialog();
	const [passwordExpired, setPasswordExpired] = useState<(ErrorType & { user: UserLogon }) | null>(
		null
	);
	const [logonIdTemp, setLogonIdTemp] = useState<string>('');
	const [countError, setCountError] = useState<number>(0);

	// eslint-disable-next-line complexity
	const loginSubmit = async (props: UserLogon, onError?: (error: any) => void) => {
		const { email = '', logonId: _logonId, rememberMe, ...rest } = props;
		const logonId = formatLogonId(_logonId || email);
		setLogonIdTemp(logonId);
		try {
			const resp = await loginFetcher(true)(
				settings?.storeId ?? '',
				{ rememberMe: props.rememberMe, updateCookies: 'true' },
				{ logonId, ...rest },
				params
			);

			if (originPathQuery === 'go_to_account' && resp && 'userId' in resp) {
				activatePostLoginDialog();
			}

			return resp;
		} catch (e) {
			const error: TransactionErrorResponse | ErrorType = processError(
				e as TransactionErrorResponse
			);

			const isOtpDeliveryFlow =
				props.x_otpFlow === OTP_METHODS.EMAIL || props.x_otpFlow === OTP_METHODS.SMS;
			if (
				isOtpDeliveryFlow &&
				isErrorType(error) &&
				(error.error.errorKey === PASSWORD_EXPIRED ||
					error.error.errorCode === ACCOUNT_LOCKOUT_ERROR)
			) {
				setPasswordExpired({ ...error, user: { ...props } });
				return error;
			}

			setCountError(countError + 1);

			if (logonIdTemp === logonId && countError > 3) {
				notifyError('Espere unos segundos antes de intentar iniciar la sesión otra vez');
			} else {
				if (logonIdTemp !== logonId) {
					setCountError(0);
				}
				if (props.changePassword) {
					const changePasswordx = true;
					const title = '';
					notifyError(error, title, changePasswordx);
				} else {
					if (onError) {
						onError(error);
					} else {
						notifyError(error);
					}
				}
			}
			return error;
		}
	};

	return {
		loginSubmit,
		passwordExpired,
		setPasswordExpired,
	};
};

export enum LOGIN_STEPS {
	INITIAL_LOGON_ID = 'INITIAL_LOGON_ID',
	SELECT_OTP_METHOD = 'SELECT_OTP_METHOD',
	LOGIN_OTP = 'LOGIN_OTP',
	LOGIN_PASSWORD = 'LOGIN_PASSWORD',
}

const GTM_AUTH_METHOD_GOOGLE: AuthMethod = 'google';

export enum OTP_METHODS {
	SMS = 'phone',
	EMAIL = 'email',
	PASSWORD = 'password',
}

export type LoginLessState = {
	step: LOGIN_STEPS;
	logonId: string;
	logonIdError?: string;
	otpMethod?: OTP_METHODS;
	lastOtpMethod?: OTP_METHODS;
	lastOtpSentAt?: number;
	lastOtpLogonId?: string;
	showPreviousMethodHint: boolean;
	otpMethodError?: string;
	otp: string;
	otpError?: string;
	password: string;
	passwordError?: string;
	submitting: boolean;
	maskedPhone?: string | null;
	maskedEmail?: string | null;
};

const initLoginLessState: LoginLessState = {
	step: LOGIN_STEPS.INITIAL_LOGON_ID,
	logonId: '',
	logonIdError: undefined,
	otpMethod: undefined,
	lastOtpMethod: undefined,
	lastOtpSentAt: undefined,
	lastOtpLogonId: undefined,
	showPreviousMethodHint: false,
	otpMethodError: undefined,
	otp: '',
	otpError: undefined,
	submitting: false,
	password: '',
	passwordError: undefined,
	maskedPhone: undefined,
	maskedEmail: undefined,
};

export const loginSendOtpFetcher =
	(pub: boolean, context?: GetServerSidePropsContext) =>
	async (
		storeId: string,
		data: {
			logonId: string;
			platform: string;
			deliveryMethod: string;
		},
		query: {
			[key: string]: string | boolean | ID[] | number;
		},
		params: RequestParams
	) => {
		try {
			const resp = await transactionsLoginIdentity(pub).loginIdentitySendOtp(
				storeId,
				data,
				query,
				params
			);
			return resp;
		} catch (error: any) {
			logError(context?.req, 'Login: loginSendOtpFetcher: error: %o', error);
			throw error;
		}
	};

export const loginMethodsFetcher =
	(pub: boolean, context?: GetServerSidePropsContext) =>
	async (
		storeId: string,
		logonId: string,
		query: {
			[key: string]: string | boolean | ID[] | number;
		},
		params: RequestParams
	) => {
		try {
			const resp = await transactionsLoginIdentity(pub).loginMethods(
				storeId,
				{ logonId },
				query,
				params
			);
			return resp;
		} catch (error: any) {
			logError(context?.req, 'Login: loginMethodsFetcher: error: %o', error);
			throw error;
		}
	};

enum LOGIN_LESS_ACTION {
	START_SUBMIT = 'START_SUBMIT',
	END_SUBMIT = 'END_SUBMIT',
	TRANSITION_STEP = 'TRANSITION_STEP',
	PARTIAL_UPDATE = 'PARTIAL_UPDATE',
}

type LoginLessAction =
	| { type: LOGIN_LESS_ACTION.START_SUBMIT }
	| { type: LOGIN_LESS_ACTION.END_SUBMIT }
	| {
			type: LOGIN_LESS_ACTION.TRANSITION_STEP;
			step: LOGIN_STEPS;
			updates?: Partial<LoginLessState>;
	  }
	| { type: LOGIN_LESS_ACTION.PARTIAL_UPDATE; updates: Partial<LoginLessState> };

const loginLessReducer = (state: LoginLessState, action: LoginLessAction): LoginLessState => {
	switch (action.type) {
		case LOGIN_LESS_ACTION.START_SUBMIT:
			return { ...state, submitting: true };
		case LOGIN_LESS_ACTION.END_SUBMIT:
			return { ...state, submitting: false };
		case LOGIN_LESS_ACTION.TRANSITION_STEP:
			return {
				...state,
				step: action.step,
				logonIdError: undefined,
				otpMethodError: undefined,
				otpError: undefined,
				passwordError: undefined,
				...action.updates,
			};
		case LOGIN_LESS_ACTION.PARTIAL_UPDATE:
			return { ...state, ...action.updates };
		default:
			return state;
	}
};

const validateLogonId = (logonId: string): string | undefined => {
	if (logonId.trim() === '') return 'Campo obligatorio';
	if (!isValidEmail(logonId)) return 'Por favor ingresa un correo electrónico válido';
	return undefined;
};

const INVALID_PASSWORD_MESSAGE =
	'The specified logon ID or password are not correct. Verify the information provided and log in again.';

const getLoginErrorMessage = (result: any): string => {
	const err = result?.error;
	if (!err) return 'unknown_error';
	const { errorKey, errorMessage } = err as { errorKey?: string; errorMessage?: string };
	if (
		errorMessage &&
		INVALID_PASSWORD_MESSAGE.toUpperCase().includes(errorMessage.toUpperCase())
	) {
		return 'invalid_password';
	}
	return errorKey || 'unknown_error';
};

const validatePassword = (password: string): string | undefined => {
	if (password.trim() === '') return 'Campo obligatorio';
	// const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{6,}$/;
	if (password.trim().length < 6) return 'Ingrese una contraseña valida';
	return undefined;
};

export const useLoginLess = () => {
	const { settings } = useSettings();
	const params = useExtraRequestParameters();
	const { notifyError } = useNotifications();
	const loginBase = useLogin();
	const { loginSubmit } = loginBase;
	const [stateLogin, dispatch] = useReducer(loginLessReducer, initLoginLessState);
	const { settings: singleSettings, loading: singleSettingsLoading } = useSingleSettings();
	const { onLoginLessBegin, onLoginLessMethod, onLoginLessSubmit } = useContext(EventsContext);
	const login_origin = useLoginOrigin();

	const baseSettings = useMemo(() => {
		const userData = singleSettings?.userData || {};
		const emailSendCooldownSeconds = getValueByPathFromUnknown(userData, [
			'otp_login_email_send_cooldown_seconds',
		]);

		const phoneSendCooldownSeconds = getValueByPathFromUnknown(userData, [
			'otp_login_phone_send_cooldown_seconds',
		]);

		return {
			emailSendCooldownSeconds: emailSendCooldownSeconds ? Number(emailSendCooldownSeconds) : 60,
			phoneSendCooldownSeconds: phoneSendCooldownSeconds ? Number(phoneSendCooldownSeconds) : 60,
		};
	}, [singleSettings?.userData]);

	const onChangeLogonId = useCallback(
		(logonId: string) =>
			dispatch({
				type: LOGIN_LESS_ACTION.PARTIAL_UPDATE,
				updates: { logonId, logonIdError: undefined },
			}),
		[]
	);

	const onBlurLogonId = useCallback(() => {
		dispatch({
			type: LOGIN_LESS_ACTION.PARTIAL_UPDATE,
			updates: { logonId: formatLogonId(stateLogin.logonId) },
		});
	}, [stateLogin.logonId]);

	const onSubmitInitialLogonId = useCallback(async () => {
		const logonIdError = validateLogonId(stateLogin.logonId);
		if (logonIdError) {
			dispatch({ type: LOGIN_LESS_ACTION.PARTIAL_UPDATE, updates: { logonIdError } });
			return;
		}

		dispatch({ type: LOGIN_LESS_ACTION.START_SUBMIT });

		onLoginLessBegin({
			gtm: {
				settings,
				login_step: 1,
				login_origin,
			},
		});

		const logonId = formatLogonId(stateLogin.logonId);

		try {
			const methods = await loginMethodsFetcher(true)(
				settings?.storeId ?? '',
				logonId,
				params,
				params
			);

			if (!methods.userExists) {
				dispatch({
					type: LOGIN_LESS_ACTION.PARTIAL_UPDATE,
					updates: { logonIdError: 'El correo no está registrado, crea tu cuenta para continuar.' },
				});
				return;
			}

			const maskedPhone = methods?.phone?.masked;
			const maskedEmail = methods?.email?.masked;
			const availableMethods = [
				OTP_METHODS.PASSWORD,
				...(methods?.email?.available && maskedEmail ? [OTP_METHODS.EMAIL] : []),
				...(methods?.phone?.available && maskedPhone ? [OTP_METHODS.SMS] : []),
			];
			const storedMethod = getOtpMethodFromStoredMethod(readStoredLoginMethod(logonId));
			const selectedMethod =
				storedMethod && availableMethods.includes(storedMethod)
					? storedMethod
					: OTP_METHODS.PASSWORD;

			if (storedMethod && selectedMethod !== storedMethod) {
				removeStoredLoginMethod(logonId);
			}

			if (!maskedPhone && !maskedEmail) {
				dispatch({
					type: LOGIN_LESS_ACTION.TRANSITION_STEP,
					step: LOGIN_STEPS.LOGIN_PASSWORD,
					updates: { password: '' },
				});
				return;
			}

			if (maskedPhone && maskedEmail) {
				dispatch({
					type: LOGIN_LESS_ACTION.TRANSITION_STEP,
					step: LOGIN_STEPS.SELECT_OTP_METHOD,
					updates: {
						maskedPhone,
						maskedEmail,
						otpMethod: selectedMethod,
						showPreviousMethodHint: Boolean(
							storedMethod && selectedMethod === storedMethod
						),
					},
				});
				return;
			}

			const deliveryMethod = maskedPhone ? OTP_METHODS.SMS : OTP_METHODS.EMAIL;

			await loginSendOtpFetcher(true)(
				settings?.storeId ?? '',
				{ logonId, platform: 'sitio', deliveryMethod },
				{},
				params
			);

				dispatch({
					type: LOGIN_LESS_ACTION.TRANSITION_STEP,
					step: LOGIN_STEPS.LOGIN_OTP,
					updates: {
						maskedPhone,
						maskedEmail,
						otpMethod: deliveryMethod,
						lastOtpMethod: deliveryMethod,
						lastOtpSentAt: Date.now(),
						lastOtpLogonId: logonId,
						otp: '',
					},
				});
		} catch (error: any) {
			notifyError(processError(error as TransactionErrorResponse));
		} finally {
			dispatch({ type: LOGIN_LESS_ACTION.END_SUBMIT });
		}
	}, [stateLogin.logonId, params, notifyError, onLoginLessBegin, settings, login_origin]);

	const onChangeOtpMethod = useCallback(
		(otpMethod: OTP_METHODS) =>
			dispatch({
				type: LOGIN_LESS_ACTION.PARTIAL_UPDATE,
				updates: {
					otpMethod,
					otpMethodError: undefined,
					password: '',
					passwordError: undefined,
				},
			}),
		[]
	);

	const onCatchError = useCallback(
		(error: any) => {
			const errorProcess = processError(error as TransactionErrorResponse);
			const isInvalidPass =
				'The specified logon ID or password are not correct. Verify the information provided and log in again.'
					.toUpperCase()
					.includes(errorProcess.error.errorMessage.toUpperCase());

			if (isInvalidPass && stateLogin.step === LOGIN_STEPS.LOGIN_OTP) {
				dispatch({
					type: LOGIN_LESS_ACTION.PARTIAL_UPDATE,
					updates: {
						otpError:
							'No pudimos validar el código, intenta nuevamente o solicita un nuevo código.',
					},
				});
				return;
			} else {
				notifyError(errorProcess);
			}
		},
		[notifyError, stateLogin.step]
	);

	const getGtmAuthMethod = (otpMethod?: OTP_METHODS) => {
		if (otpMethod === OTP_METHODS.SMS) return 'otp_sms';
		if (otpMethod === OTP_METHODS.EMAIL) return 'otp_email';
		return 'password';
	};

	const getOtpMethodFromStoredMethod = (method?: StoredLoginMethod): OTP_METHODS | undefined => {
		if (method === 'phone') return OTP_METHODS.SMS;
		if (method === 'email') return OTP_METHODS.EMAIL;
		if (method === 'password') return OTP_METHODS.PASSWORD;
		return undefined;
	};

	const getOtpCooldownSeconds = (otpMethod: OTP_METHODS) =>
		otpMethod === OTP_METHODS.EMAIL
			? baseSettings.emailSendCooldownSeconds
			: baseSettings.phoneSendCooldownSeconds;

	const onSubmitOtpMethod = useCallback(
		async (data?: Partial<UserLogon>) => {
			if (!stateLogin.otpMethod) {
				dispatch({
					type: LOGIN_LESS_ACTION.PARTIAL_UPDATE,
					updates: { otpMethodError: 'Selecciona un método de OTP' },
				});
				return;
			}

			onLoginLessMethod({
				gtm: {
					settings,
					login_step: 2,
					auth_method: getGtmAuthMethod(stateLogin.otpMethod),
					login_origin,
				},
			});

			if (stateLogin.otpMethod === OTP_METHODS.PASSWORD) {
				const passwordError = validatePassword(stateLogin.password);
				if (passwordError) {
					dispatch({
						type: LOGIN_LESS_ACTION.PARTIAL_UPDATE,
						updates: { passwordError },
					});
					return;
				}

				dispatch({ type: LOGIN_LESS_ACTION.START_SUBMIT });

				try {
					const result = await loginSubmit(
						{
							...data,
							logonId: stateLogin.logonId,
							logonPassword: stateLogin.password,
							rememberMe: false,
						},
						onCatchError
					);

					const isSuccess = 'userId' in (result ?? {});
					if (isSuccess) {
						writeStoredLoginMethod(stateLogin.logonId, OTP_METHODS.PASSWORD);
					}
					onLoginLessSubmit({
						gtm: {
							settings,
							login_step: 3,
							auth_method: getGtmAuthMethod(stateLogin.otpMethod),
							login_status: isSuccess ? 'success' : 'error',
							is_new_user: false,
							login_origin,
							...(isSuccess ? {} : { error_message: getLoginErrorMessage(result) }),
						},
					});

					return result;
				} finally {
					dispatch({ type: LOGIN_LESS_ACTION.END_SUBMIT });
				}
			}

			dispatch({ type: LOGIN_LESS_ACTION.START_SUBMIT });

			const logonId = formatLogonId(stateLogin.logonId);
			const sendCooldownSeconds = getOtpCooldownSeconds(stateLogin.otpMethod);
			const canReuseCurrentOtp =
				stateLogin.lastOtpMethod === stateLogin.otpMethod &&
				stateLogin.lastOtpLogonId === logonId &&
				typeof stateLogin.lastOtpSentAt === 'number' &&
				Date.now() - stateLogin.lastOtpSentAt < sendCooldownSeconds * 1000;

			try {
				if (canReuseCurrentOtp) {
					dispatch({
						type: LOGIN_LESS_ACTION.TRANSITION_STEP,
						step: LOGIN_STEPS.LOGIN_OTP,
						updates: { otpMethod: stateLogin.otpMethod },
					});
					return;
				}

				await loginSendOtpFetcher(true)(
					settings?.storeId ?? '',
					{ logonId, platform: 'sitio', deliveryMethod: stateLogin.otpMethod },
					{},
					params
				);

				dispatch({
					type: LOGIN_LESS_ACTION.TRANSITION_STEP,
					step: LOGIN_STEPS.LOGIN_OTP,
					updates: {
						otpMethod: stateLogin.otpMethod,
						lastOtpMethod: stateLogin.otpMethod,
						lastOtpSentAt: Date.now(),
						lastOtpLogonId: logonId,
						otp: '',
					},
				});
			} catch (e) {
				notifyError(processError(e as TransactionErrorResponse));
			} finally {
				dispatch({ type: LOGIN_LESS_ACTION.END_SUBMIT });
			}
		},
		[
			stateLogin.otpMethod,
			stateLogin.lastOtpMethod,
			stateLogin.lastOtpLogonId,
			stateLogin.lastOtpSentAt,
			stateLogin.logonId,
			params,
			notifyError,
			stateLogin.password,
			loginSubmit,
			onCatchError,
			onLoginLessSubmit,
			onLoginLessMethod,
			settings,
			login_origin,
			baseSettings.emailSendCooldownSeconds,
			baseSettings.phoneSendCooldownSeconds,
		]
	);

	const onChangeOtp = useCallback(
		(otp: string) =>
			dispatch({ type: LOGIN_LESS_ACTION.PARTIAL_UPDATE, updates: { otp, otpError: undefined } }),
		[]
	);

	const onToggleLoginPassword = useCallback(
		() =>
			dispatch({
				type: LOGIN_LESS_ACTION.TRANSITION_STEP,
				step: LOGIN_STEPS.LOGIN_PASSWORD,
				updates: { password: '' },
			}),
		[]
	);

	const onToggleBackStep = useCallback(() => {
		const previousStep =
			stateLogin.step === LOGIN_STEPS.LOGIN_OTP && stateLogin.maskedPhone && stateLogin.maskedEmail
				? LOGIN_STEPS.SELECT_OTP_METHOD
				: LOGIN_STEPS.INITIAL_LOGON_ID;

		dispatch({
			type: LOGIN_LESS_ACTION.TRANSITION_STEP,
			step: previousStep,
			updates:
				previousStep === LOGIN_STEPS.SELECT_OTP_METHOD
					? {
							otpMethod: stateLogin.otpMethod,
							showPreviousMethodHint: Boolean(stateLogin.otpMethod),
					  }
					: undefined,
		});
	}, [stateLogin.maskedEmail, stateLogin.maskedPhone, stateLogin.otpMethod, stateLogin.step]);

	const onToggleSelectOtpMethod = useCallback(async () => {
		dispatch({ type: LOGIN_LESS_ACTION.START_SUBMIT });

		try {
			if (stateLogin.maskedPhone && stateLogin.maskedEmail) {
				dispatch({
					type: LOGIN_LESS_ACTION.TRANSITION_STEP,
					step: LOGIN_STEPS.SELECT_OTP_METHOD,
					updates: {
						otpMethod: stateLogin.otpMethod,
						showPreviousMethodHint: Boolean(stateLogin.otpMethod),
					},
				});
				return;
			}

			const deliveryMethod = stateLogin.maskedPhone ? OTP_METHODS.SMS : OTP_METHODS.EMAIL;

			const logonId = formatLogonId(stateLogin.logonId);

			await loginSendOtpFetcher(true)(
				settings?.storeId ?? '',
				{ logonId, platform: 'sitio', deliveryMethod },
				{},
				params
			);

			dispatch({
				type: LOGIN_LESS_ACTION.TRANSITION_STEP,
				step: LOGIN_STEPS.LOGIN_OTP,
				updates: { otp: '' },
			});
		} catch (e) {
			notifyError(processError(e as TransactionErrorResponse));
		} finally {
			dispatch({ type: LOGIN_LESS_ACTION.END_SUBMIT });
		}
	}, [
		stateLogin.maskedPhone,
		stateLogin.maskedEmail,
		stateLogin.otpMethod,
		stateLogin.logonId,
		settings?.storeId,
		params,
		notifyError,
	]);

	const onChangePassword = useCallback(
		(password: string) =>
			dispatch({
				type: LOGIN_LESS_ACTION.PARTIAL_UPDATE,
				updates: { password, passwordError: undefined },
			}),
		[]
	);

	const onSubmitLogin = useCallback(
		async (data?: Partial<UserLogon>) => {
			const logonIdError = validateLogonId(stateLogin.logonId);
			if (logonIdError) {
				dispatch({ type: LOGIN_LESS_ACTION.PARTIAL_UPDATE, updates: { logonIdError } });
				return;
			}

			if (stateLogin.step === LOGIN_STEPS.LOGIN_OTP && stateLogin.otp.trim().length !== 6) {
				dispatch({
					type: LOGIN_LESS_ACTION.PARTIAL_UPDATE,
					updates: { otpError: 'Por favor ingresa el OTP de 6 dígitos' },
				});
				return;
			}

			if (stateLogin.step === LOGIN_STEPS.LOGIN_PASSWORD) {
				const passwordError = validatePassword(stateLogin.password);
				if (passwordError) {
					dispatch({
						type: LOGIN_LESS_ACTION.PARTIAL_UPDATE,
						updates: { passwordError },
					});
					return;
				}
			}

			dispatch({ type: LOGIN_LESS_ACTION.START_SUBMIT });

				try {
					const response = await loginSubmit(
						{
							...data,
							logonId: stateLogin.logonId,
						logonPassword:
							stateLogin.step === LOGIN_STEPS.LOGIN_PASSWORD ? stateLogin.password : stateLogin.otp,
							rememberMe: false,
							x_otpFlow: stateLogin.otpMethod,
						},
						onCatchError
					);

					const isSuccess = 'userId' in (response ?? {});
					const successfulMethod =
						stateLogin.step === LOGIN_STEPS.LOGIN_PASSWORD
							? OTP_METHODS.PASSWORD
							: stateLogin.otpMethod;
					if (isSuccess && successfulMethod) {
						writeStoredLoginMethod(stateLogin.logonId, successfulMethod);
					}
					onLoginLessSubmit({
						gtm: {
							settings,
							login_step: 3,
							auth_method: getGtmAuthMethod(stateLogin.otpMethod),
							login_status: isSuccess ? 'success' : 'error',
							is_new_user: false,
							login_origin,
						...(isSuccess ? {} : { error_message: getLoginErrorMessage(response) }),
					},
				});

				return response;
			} finally {
				dispatch({ type: LOGIN_LESS_ACTION.END_SUBMIT });
			}
		},
		[
			stateLogin.password,
			stateLogin.logonId,
			loginSubmit,
			stateLogin.step,
			stateLogin.otp,
			stateLogin.otpMethod,
			onCatchError,
			onLoginLessSubmit,
			settings,
			login_origin,
		]
	);

	const onSubmitGoogleLogin = useCallback(
		async (idToken: string, data?: Partial<UserLogon>) => {
			onLoginLessMethod({
				gtm: {
					settings,
					login_step: 2,
					auth_method: GTM_AUTH_METHOD_GOOGLE,
					login_origin,
				},
			});

			dispatch({ type: LOGIN_LESS_ACTION.START_SUBMIT });

			try {
				const { email } = decodeGoogleIdToken(idToken);
				const response = await loginSubmit({
					...data,
					logonId: email,
					logonPassword: 'google',
					extendedProperties: 'google_token',
					googleIdToken: idToken,
					rememberMe: false,
				});

				const isSuccess = 'userId' in (response ?? {});
				onLoginLessSubmit({
					gtm: {
						settings,
						login_step: 3,
						auth_method: GTM_AUTH_METHOD_GOOGLE,
						login_status: isSuccess ? 'success' : 'error',
						is_new_user: false,
						login_origin,
						...(isSuccess ? {} : { error_message: getLoginErrorMessage(response) }),
					},
				});

				return response;
			} finally {
				dispatch({ type: LOGIN_LESS_ACTION.END_SUBMIT });
			}
		},
		[loginSubmit, onLoginLessMethod, onLoginLessSubmit, settings, login_origin]
	);

	const { visible: nextRouteLoading } = useIsLoadingBarNextRoute({
		startDelayMs: 50,
		finishHoldMs: 500,
	});

	return {
		stateLogin,
		baseSettings,
		singleSettingsLoading,
		nextRouteLoading,
		onChangeLogonId,
		onBlurLogonId,
		onSubmitInitialLogonId,
		onChangeOtpMethod,
		onSubmitOtpMethod,
		onChangeOtp,
		onToggleLoginPassword,
		onToggleSelectOtpMethod,
		onChangePassword,
		onToggleBackStep,
		onSubmitLogin,
		onSubmitGoogleLogin,

		//  base
		loginBase,
	};
};
